import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';

// Helper function to get color based on biller name (for dynamic billers)
const getBillerColor = (billerName) => {
    const colors = ["#0277BD", "#01579B", "#006064", "#004D40", "#1B5E20", "#E65100", "#B71C1C", "#311B92", "#4527A0", "#F57F17", "#0D47A1", "#BF360C", "#2E7D32", "#C62828", "#283593"];
    let hash = 0;
    for (let i = 0; i < billerName.length; i++) {
        hash = billerName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
};

const getBillerLogo = (billerName) => {
    // Get first two letters of each word
    const words = billerName.split(' ');
    if (words.length === 1) {
        return billerName.substring(0, 2).toUpperCase();
    }
    return (words[0].charAt(0) + (words[1].charAt(0) || words[0].charAt(1))).toUpperCase();
};

// All available categories
const ALL_CATEGORIES = [
    "Electricity",
    "Fastag",
    "Gas",
    "Broadband Postpaid",
    "Cable TV",
    "Clubs and Associations",
    "Credit Card",
    "Education Fees",
    "Health Insurance",
    "Hospital",
    "Hospital and Pathology",
    "Housing Society",
    "Insurance",
    "Landline Postpaid",
    "Life Insurance",
    "Loan Repayment",
    "LPG Gas",
    "Mobile Postpaid",
    "Municipal Services",
    "Municipal Taxes",
    "Postpaid",
    "Recurring Deposit",
    "Subscription",
    "Water",
    "Donation",
    "Prepaid Meter"
];

// localStorage key
const STORAGE_KEY = "selected_bill_category";

function Operator() {
    const navigate = useNavigate();
    const location = useLocation();

    // Initialize category from localStorage, then location.state, then default
    const getInitialCategory = () => {
        // First check if there's a category in location.state (from navigation)
        if (location.state?.category) {
            return location.state.category;
        }
        // Then check localStorage for saved category
        const savedCategory = localStorage.getItem(STORAGE_KEY);
        if (savedCategory && ALL_CATEGORIES.includes(savedCategory)) {
            return savedCategory;
        }
        // Default to Electricity
        return "Electricity";
    };

    const [activeCategory, setActiveCategory] = useState(getInitialCategory);
    const [searchQuery, setSearchQuery] = useState("");
    const [billers, setBillers] = useState([]);
    const [loadingBillers, setLoadingBillers] = useState(false);
    const [showAllCategories, setShowAllCategories] = useState(false);

    // Display categories (initially show first 7, then all when expanded)
    const displayedCategories = showAllCategories ? ALL_CATEGORIES : ALL_CATEGORIES.slice(0, 7);

    // Save category to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, activeCategory);
    }, [activeCategory]);

    // Fetch billers whenever category changes
    useEffect(() => {
        const fetchBillers = async () => {
            setLoadingBillers(true);
            setBillers([]);
            setSearchQuery("");

            try {
                const result = await apiCall(
                    "operator",
                    {
                        category: activeCategory
                    },
                    true
                );

                if (result.status === true && result.response_code === 1) {
                    setBillers(result.data || []);
                    if (result.data?.length === 0) {
                        toast.error(`No ${activeCategory} billers found`);
                    }
                } else {
                    toast.error(result.message || `Failed to fetch ${activeCategory} billers`);
                    setBillers([]);
                }
            } catch (error) {
                console.error("API Error:", error);
                toast.error("Network error. Please try again.");
                setBillers([]);
            } finally {
                setLoadingBillers(false);
            }
        };

        fetchBillers();
    }, [activeCategory]);

    const filteredBillers = useMemo(() => {
        if (!searchQuery.trim()) return billers;
        return billers.filter((biller) =>
            biller.biller_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [billers, searchQuery]);

    const handleOperatorClick = (biller) => {
        // Navigate directly to operator-form.js with operator_id
        navigate("/operator-form", {
            state: {
                operator_id: biller.operator_id,
                biller_name: biller.biller_name,
                category: activeCategory,
                logo: biller.logo
            }
        });
    };

    const handleCategoryChange = (category) => {
        setActiveCategory(category);
        setShowAllCategories(false);
    };

    const renderBillerList = () => (
        <div>
            {/* Category Tabs - Scrollable */}
            <div className="overflow-x-auto scrollbar-hide mb-4">
                <div className="flex flex-wrap gap-2">
                    {displayedCategories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => handleCategoryChange(cat)}
                            className="px-4 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-all duration-200"
                            style={{
                                background: activeCategory === cat ? APP_THEME.primaryColor : APP_THEME.surfaceColor,
                                color: activeCategory === cat ? APP_THEME.textOnPrimary : APP_THEME.textPrimary,
                                border: `1px solid ${activeCategory === cat ? APP_THEME.primaryColor : APP_THEME.borderColor}`,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                        >
                            {cat}
                        </button>
                    ))}
                    {!showAllCategories && ALL_CATEGORIES.length > 7 && (
                        <button
                            onClick={() => setShowAllCategories(true)}
                            className="px-4 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-all duration-200"
                            style={{
                                background: APP_THEME.surfaceColor,
                                color: APP_THEME.primaryColor,
                                border: `1px solid ${APP_THEME.primaryColor}`,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                        >
                            +{ALL_CATEGORIES.length - 7} More
                        </button>
                    )}
                    {showAllCategories && (
                        <button
                            onClick={() => setShowAllCategories(false)}
                            className="px-4 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-all duration-200"
                            style={{
                                background: APP_THEME.surfaceColor,
                                color: APP_THEME.primaryColor,
                                border: `1px solid ${APP_THEME.primaryColor}`,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                        >
                            Show Less
                        </button>
                    )}
                </div>
            </div>

            {/* Search Bar */}
            <div className="flex items-center bg-surface rounded-xl px-3.5 mb-4 transition-colors duration-200"
                style={{
                    background: APP_THEME.surfaceColor,
                    borderColor: APP_THEME.borderColor,
                }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                    type="text"
                    placeholder={`Search ${activeCategory} billers...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 py-3 px-2.5 border-none text-sm font-medium outline-none bg-transparent"
                    style={{
                        color: APP_THEME.textPrimary,
                        fontFamily: APP_THEME.fontFamily,
                    }}
                />
                {searchQuery && (
                    <div onClick={() => setSearchQuery("")} className="cursor-pointer p-1">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </div>
                )}
            </div>

            {/* Biller List */}
            <div className="flex flex-col gap-2">
                {loadingBillers && (
                    <div className="text-center py-12">
                        <div className="w-10 h-10 rounded-full animate-spin mx-auto mb-4" style={{ border: `3px solid ${APP_THEME.primaryColor}33`, borderTopColor: APP_THEME.primaryColor }} />
                        <div className="text-sm font-medium" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            Loading {activeCategory} billers...
                        </div>
                    </div>
                )}

                {!loadingBillers && filteredBillers.length === 0 && (
                    <div className="text-center py-12 px-4">

                        <div className="mb-3 flex justify-center">
                            <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={APP_THEME.textSecondary}
                                strokeWidth="1.5"
                            >
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </div>

                        <div
                            className="text-sm font-medium"
                            style={{
                                color: APP_THEME.textSecondary,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                        >
                            {searchQuery
                                ? `No billers found for "${searchQuery}"`
                                : `No ${activeCategory} billers available`}
                        </div>
                    </div>
                )}

                {!loadingBillers && filteredBillers.map((biller) => {
                    const billerName = biller.biller_name;
                    const logo = biller.logo;
                    const info = {
                        logo: getBillerLogo(billerName),
                        color: getBillerColor(billerName)
                    };

                    return (
                        <div
                            key={biller.operator_id}
                            onClick={() => handleOperatorClick(biller)}
                            className="p-3.5 px-4 bg-surface rounded-xl border cursor-pointer flex items-center gap-3.5 transition-all duration-200 active:scale-98 hover:shadow-md"
                            style={{
                                background: APP_THEME.surfaceColor,
                                borderColor: APP_THEME.borderColor,
                            }}
                        >
                            {/* Biller Logo */}
                            {logo ? (
                                <img
                                    src={logo}
                                    alt={billerName}
                                    className="w-11 h-11 rounded-xl object-cover flex-shrink-0"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        if (e.target.nextSibling) {
                                            e.target.nextSibling.style.display = 'flex';
                                        }
                                    }}
                                />
                            ) : null}
                            <div
                                className="w-11 h-11 rounded-xl flex items-center justify-center text-[14px] font-extrabold text-white flex-shrink-0"
                                style={{
                                    background: info.color,
                                    fontFamily: APP_THEME.fontFamily,
                                    display: logo ? 'none' : 'flex'
                                }}
                            >
                                {info.logo}
                            </div>

                            <div className="flex-1">
                                <span className="text-sm block" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                                    {billerName}
                                </span>
                            </div>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2.5">
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <>
            <Header title={`Pay ${activeCategory} Bill`} showBack={true} />
            <div className="p-4 pb-8">
                {renderBillerList()}

                <Toaster
                    position="bottom-center"
                    toastOptions={{
                        duration: 3000,
                        style: {
                            fontFamily: APP_THEME.fontFamily,
                            borderRadius: '12px',
                        },
                    }}
                />

                <style>
                    {`
                        .active\\:scale-98:active {
                            transform: scale(0.98);
                        }
                        .scrollbar-hide::-webkit-scrollbar {
                            display: none;
                        }
                        .scrollbar-hide {
                            -ms-overflow-style: none;
                            scrollbar-width: none;
                        }
                        @keyframes spin {
                            to { transform: rotate(360deg); }
                        }
                        .animate-spin {
                            animation: spin 1s linear infinite;
                        }
                    `}
                </style>
            </div>
        </>
    );
}

export default Operator;